import { Stack, Typography } from '@mui/material'
import { useCurrentStore, useEditOrderItem, useOrdering } from 'hooks'
import { MenuCategory, MenuItem } from 'models'
import { FC, MutableRefObject, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addNonEditableItemToOrder,
  calculateCategorySchedules,
  checkCanShowEditItemDialog,
} from 'services'
import { MenuItemsGrid } from './MenuItemsGrid'
import { MenuItemsList } from './MenuItemsList'

interface MenuCategorySectionProps {
  category: MenuCategory
  index: number
  viewType: 'grid' | 'list'
  sectionsRef: MutableRefObject<(HTMLElement | null)[]>
}

export const MenuCategorySection: FC<MenuCategorySectionProps> = ({
  category,
  index,
  viewType,
  sectionsRef,
}) => {
  const { t } = useTranslation()
  const { store } = useCurrentStore()
  const { order, saveOrder } = useOrdering()
  const { addOrderItem } = useEditOrderItem()

  const unavailableMessage = useMemo(
    () => calculateCategorySchedules(store, category, order),
    [store, category, order],
  )

  const handleItemClick = (item: MenuItem) => {
    if (!order) return

    if (checkCanShowEditItemDialog(item, store)) {
      addOrderItem(category, item)
    } else {
      saveOrder(addNonEditableItemToOrder(item, category, order))
    }
  }

  return (
    <Stack
      key={category.id}
      ref={(el) => (sectionsRef.current[index] = el)}
      sx={{ pb: 4 }}
    >
      <Typography
        variant="h4"
        sx={{ marginTop: 4, marginBottom: category.description ? 1 : 4 }}
      >
        {category.name}
      </Typography>
      {category.description && (
        <Typography variant="body2" sx={{ marginBottom: 4 }}>
          {category.description}
        </Typography>
      )}
      {viewType === 'list' ? (
        <MenuItemsList
          items={category.items ?? []}
          onItemClick={(item) => {
            handleItemClick(item)
          }}
          unavailableMessage={t(unavailableMessage)}
        />
      ) : (
        <MenuItemsGrid
          items={category.items ?? []}
          onItemClick={(item) => {
            handleItemClick(item)
          }}
          unavailableMessage={t(unavailableMessage)}
        />
      )}
    </Stack>
  )
}
